import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { getItem, removeItem, setItem } from '~/services/localStorage';

interface FormStorageOptions {
  key: string;
  debounceTime?: number;
  conditionKey?: string;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const useFormStorage = <T extends object>(
  form: UseFormReturn<T>,
  options: FormStorageOptions,
  defaultValues = {} as T,
): { clearDraft: () => void; isRestored: boolean } => {
  const { key, debounceTime = 1000, conditionKey } = options;
  const { watch, reset } = form;
  const [isRestored, setIsRestored] = useState(false);

  const defaultValuesRef = useRef(defaultValues);

  useEffect(() => {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);

  // Tracks whether the draft has already been applied — prevents double-reset
  // when isRestored changes (isRestored is kept in deps so clearDraft() triggers re-check)
  const hasRestoredFromDraftRef = useRef(false);

  const formData = watch();

  const saveToLocalStorage = useCallback(() => {
    setItem(key, formData);
  }, [key, formData]);

  const needRestore = useMemo(() => {
    const storedData = getItem(key);

    if (!conditionKey || !storedData) {
      return true;
    }

    return (
      conditionKey && storedData[conditionKey] === (formData as typeof storedData)?.[conditionKey]
    );
  }, [formData, key, conditionKey]);

  // Restore form data from local storage on component mount
  useEffect(() => {
    // Skip if draft was already applied and the form is shown (guards against double-reset
    // caused by isRestored being in deps, and against re-restoration on formData changes)
    if (hasRestoredFromDraftRef.current && isRestored) return;

    const storedData = getItem(key);
    if (storedData && needRestore && !hasRestoredFromDraftRef.current) {
      hasRestoredFromDraftRef.current = true;
      try {
        const parsedData = storedData;

        // keepDefaultValues: true preserves the form's baseline (server values) so that
        // restored fields are considered dirty — this lets react-hook-form's keepDirtyValues
        // option prevent server updates from overwriting the draft after restoration.
        reset({ ...defaultValuesRef.current, ...parsedData }, { keepDefaultValues: true });
      } catch (e) {
        removeItem(key);
      }
    }

    // Always mark as restored so the consuming component can render its content.
    // Previously this was only set inside the try block, causing the form to remain
    // hidden forever if reset() threw an error.
    setIsRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, needRestore, isRestored, reset]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      saveToLocalStorage();
    }, debounceTime);

    return () => clearTimeout(timerId);
  }, [saveToLocalStorage, debounceTime]);

  const clearDraft = () => {
    removeItem(key);
    hasRestoredFromDraftRef.current = false;
    setIsRestored(false);
  };

  return { isRestored, clearDraft };
};

export default useFormStorage;
